import { Link } from "react-router-dom";

export default function HowToPayOffCreditCardDebtFast() {
    return (
        <>
            <p>
                Credit card debt is uniquely punishing. Unlike a mortgage or a student loan, the interest
                compounds daily, the minimum payments are designed to keep you in debt for decades, and
                a 22%–29% APR will quietly erase any investment return you might earn elsewhere. The
                good news: with a deliberate plan, most households can clear a $5,000–$20,000 balance in
                12–24 months. Here's the framework that actually works.
            </p>

            <h2>Step 1: Stop the bleeding before you treat the wound</h2>
            <p>
                Before you put a single extra dollar toward payoff, you have to stop adding to the
                balance. That means: take the cards out of your wallet, remove them from autofill in
                Apple/Google Pay, and switch any recurring subscriptions to a debit card. If a card
                isn't physically accessible, it can't be used impulsively. This step alone fixes about
                half of all credit card debt problems.
            </p>

            <h2>Step 2: List every card, balance, and APR</h2>
            <p>
                Write down — actually write down — every card you carry, the current balance, and the
                APR. APR matters more than balance. A $2,000 balance at 29% will cost you more in
                interest over a year than a $4,000 balance at 14%.
            </p>

            <h2>Step 3: Pick avalanche or snowball — and commit</h2>
            <p>
                The two proven payoff methods both work. The right one depends on your psychology.
            </p>
            <ul>
                <li>
                    <strong>Avalanche method:</strong> Pay minimums on everything, then throw every
                    extra dollar at the highest-APR card first. Mathematically optimal — saves the
                    most money. Best if you're motivated by efficiency.
                </li>
                <li>
                    <strong>Snowball method:</strong> Pay minimums on everything, then throw every
                    extra dollar at the smallest balance first, regardless of rate. You'll pay slightly
                    more in interest, but you'll clear cards faster — and each "win" creates momentum.
                    Best if you've tried and failed before.
                </li>
            </ul>
            <p>
                Run your specific numbers in our <Link to="/calculator/debt-payoff">debt payoff calculator</Link> to
                see exactly how many months each method takes for your balances. Most people pick
                avalanche when they see the side-by-side and realize the snowball costs an extra
                $400–$800 — but if avalanche doesn't motivate you, snowball wins simply because
                it's the one you'll actually stick with.
            </p>

            <h2>Step 4: Call and ask for a lower rate</h2>
            <p>
                This is the single highest-ROI 10 minutes of any debt payoff plan, and almost no one
                does it. Call the number on the back of each card. The script:
            </p>
            <p>
                <em>"Hi, I've been a customer for X years. I'm working on paying down my balance and I'd
                like to request an APR reduction. What's the best rate you can offer me today?"</em>
            </p>
            <p>
                Success rate: roughly 50% on the first call, especially with longstanding accounts.
                Typical reduction: 3–7 percentage points. On a $10,000 balance, dropping from 24% to
                18% saves about $600/year in interest. That's a $3,600/hour effective wage for a 10-minute
                phone call.
            </p>

            <h2>Step 5: Consider a 0% balance transfer (carefully)</h2>
            <p>
                If you have decent credit (680+), a 0% APR balance transfer card can move your balance
                into a 12–21 month interest-free runway. The catch: there's usually a 3–5% transfer fee
                upfront, and if you don't pay it off before the promo expires, the rate snaps back —
                sometimes higher than your original card.
            </p>
            <p>
                The math only works if you have a realistic plan to clear the balance within the promo
                period. If you transfer $10,000 to a 15-month 0% card with a 3% fee, that's $300 in
                fees vs. roughly $2,500 in interest you'd otherwise pay on a 24% APR card. Strong win
                — but only if you actually pay it off.
            </p>

            <h2>Step 6: Use windfalls for principal-only payments</h2>
            <p>
                Tax refunds. Work bonuses. Birthday money. Stimulus checks. Side hustle income. Any
                unexpected cash should go directly to your highest-rate card as a principal payment —
                <em>not</em> spread across your normal payment cycle. A single $1,500 windfall applied
                to a 24% APR balance shaves 3–5 months off your payoff timeline.
            </p>

            <h2>Worked example: $8,000 across three cards at 22% average APR</h2>
            <p>
                With minimum payments only (~$200/mo): <strong>11+ years</strong> to payoff,{" "}
                <strong>$8,400</strong> in interest.
            </p>
            <p>
                With $200 minimums + $300 extra/month (avalanche): <strong>20 months</strong> to payoff,{" "}
                <strong>$1,650</strong> in interest. <em>Savings: $6,750 and 9+ years of your life.</em>
            </p>
            <p>
                With the same payment + one 7-percentage-point rate reduction call: <strong>18 months</strong>{" "}
                to payoff, <strong>$1,150</strong> in interest.
            </p>

            <h2>What NOT to do</h2>
            <ul>
                <li>
                    <strong>Don't pause retirement contributions to the point of losing employer match.</strong>{" "}
                    A 100% match is a guaranteed 100% return — that beats even a 29% credit card.
                </li>
                <li>
                    <strong>Don't take a HELOC or 401(k) loan to clear cards unless your situation is dire.</strong>{" "}
                    HELOCs turn unsecured debt into debt that's collateralized by your house. 401(k)
                    loans come due if you leave your job — and then become taxable distributions if you can't repay.
                </li>
                <li>
                    <strong>Don't close cards after paying them off.</strong> Keep them open with a small
                    recurring autopaid charge (like a $5 streaming service). Closing cards drops your
                    credit utilization ratio and can ding your score by 20–40 points.
                </li>
                <li>
                    <strong>Don't chase debt-consolidation gimmicks.</strong> The legitimate version is a
                    balance transfer card or a personal loan with a lower rate. The illegitimate version
                    is "debt settlement" — they negotiate a partial payoff with your creditors but
                    destroy your credit and create a tax bill on the forgiven amount.
                </li>
            </ul>

            <h2>After you're debt-free</h2>
            <p>
                The same $500/month you were throwing at credit cards becomes the seed of a real
                emergency fund and retirement balance. Redirect it immediately — don't let lifestyle
                inflation absorb it. See our <Link to="/calculator/emergency-fund">emergency fund calculator</Link> for
                a personalized target, then start hitting the{" "}
                <Link to="/calculator/roth-vs-traditional">Roth vs. Traditional calculator</Link> to decide
                where new dollars go.
            </p>

            <h2>Tools to run your own numbers</h2>
            <ul>
                <li><Link to="/calculator/debt-payoff">Debt payoff calculator</Link> — model avalanche vs snowball</li>
                <li><Link to="/calculator/credit-card-payoff">Single-card payoff calculator</Link> — for one balance, extra payments</li>
                <li><Link to="/calculator/loan">Personal loan calculator</Link> — for a consolidation-loan scenario</li>
                <li><Link to="/calculator/emergency-fund">Emergency fund calculator</Link> — the next step after payoff</li>
            </ul>

            <p className="text-sm text-muted-foreground italic mt-8">
                Credit card terms and promotional rates change frequently. Always verify current APRs,
                balance transfer fees, and promo periods directly with your card issuer before making a
                consolidation decision.
            </p>
        </>
    );
}
